<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="deleteUserTaskDialog"
            :data="userTask"
            @on-confirm="removeUserTask(userTask)"
            @on-abort="closeDeleteUserTaskDialog"
        >
            <template #title>
                Usuwanie zadania
            </template>
            <template #content>
                Czy na pewno chcesz usunąć zadanie?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <UserTaskDialog
            :is-open="userTaskDialog"
            :user-task-data="userTask"
            :action-name="actionName"
            :action="action"
            @get-user-tasks="getUserTasks"
            @on-close="closeUserTaskDialog"
        />

        <page-title>Lista zadań</page-title>

        <v-row align="center">
            <v-col cols="12" sm="4">
                <v-btn
                    v-if="canCreateUserTask"
                    color="secondary"
                    medium
                    data-sentry-label="Dodaj zadanie"
                    @click="showUserTaskDialog()"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Dodaj zadanie
                </v-btn>
            </v-col>

            <v-col cols="12" sm="8">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col sm="auto">
                            <v-checkbox
                                v-model="filters.userTaskStatus"
                                label="Ukryj wykonane"
                                hide-details
                                class="mt-2"
                                @change="changeFilter"
                            />
                        </v-col>
                        <v-col v-if="canReadUserTask" cols="12" md="4" lg="4">
                            <v-autocomplete
                                v-model="filters.responsibleUser"
                                :items="users"
                                label="Wykonawca"
                                :filter="customUsersFilter"
                                :loading="usersSearchLoading"
                                :search-input.sync="usersSearch"
                                class="v-text-field--placeholder-dark"
                                item-value="id"
                                no-data-text="Nie znaleziono"
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @input="changeFilter"
                                @click:clear="clearFilter('responsibleUser')"
                            >
                                <template slot="no-data">
                                    <v-list-item>
                                        <v-list-item-title>
                                            <template v-if="!usersSearch">
                                                Wpisz treść aby odnaleźć <strong>pracownika</strong>.
                                            </template>
                                            <template v-else-if="usersSearchLoading">
                                                Trwa wyszukiwanie...
                                            </template>
                                            <template v-else>
                                                Nie znaleziono
                                            </template>
                                        </v-list-item-title>
                                    </v-list-item>
                                </template>
                                <template slot="selection" slot-scope="data">
                                    <template v-if="data.item.firstName && data.item.lastName">
                                        {{ data.item.firstName }} {{ data.item.lastName }}
                                    </template>
                                    <template v-else>
                                        {{ data.item.username }}
                                    </template>
                                </template>
                                <template slot="item" slot-scope="data">
                                    <template v-if="data.item.firstName && data.item.lastName">
                                        {{ data.item.firstName }} {{ data.item.lastName }}
                                    </template>
                                    <template v-else>
                                        {{ data.item.username }}
                                    </template>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="userTasks"
                        :loading="userTasksLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :mobile-breakpoint="0"
                        name="userTasksData"
                        @pagination="updatePagination"
                    >
                        <template #item="{item}">
                            <tr>
                                <td>
                                    {{ item.title }}
                                </td>
                                <td>
                                    {{ formatDate(item.plannedFinishDate, withTime = false) }}
                                </td>
                                <td>
                                    <template v-if="item.responsibleUser.firstName && item.responsibleUser.lastName">
                                        {{ item.responsibleUser.firstName }} {{ item.responsibleUser.lastName }}
                                    </template>
                                    <template v-else>
                                        {{ item.responsibleUser.username }}
                                    </template>
                                </td>
                                <td>
                                    {{ formatDate(item.finishDate) }}
                                </td>
                                <td class="text-center">
                                    <div class="d-inline-flex">
                                        <v-tooltip :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    data-sentry-label="Szczegóły zadania"
                                                    v-on="on"
                                                    @click="showOpenTask(item.id)"
                                                >
                                                    <v-icon color="blue">
                                                        mdi-ballot
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Szczegóły zadania</span>
                                        </v-tooltip>
                                        <v-tooltip :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    v-if="canUpdateUserTask"
                                                    icon
                                                    data-sentry-label="Edytuj"
                                                    v-on="on"
                                                    @click="showEditUserTask(item.id)"
                                                >
                                                    <v-icon color="teal">
                                                        mdi-pencil
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Edytuj</span>
                                        </v-tooltip>
                                        <v-tooltip top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    v-if="canDeleteUserTask"
                                                    icon
                                                    data-sentry-label="Usuń"
                                                    v-on="on"
                                                    @click="showDeleteUserTask(item.id)"
                                                >
                                                    <v-icon color="pink">
                                                        mdi-delete
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Usuń</span>
                                        </v-tooltip>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import UserTaskDialog from './UserTaskDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog'

import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import dateFormatter from '@/mixins/dateFormatter'
import customFilters from '@/mixins/customFilters'

import { hasUserPermission } from '@/utils/permissionsHandler'
import { EventBus } from '@/utils/eventBus'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'

let searchTimer

export default {
    name: 'UserTasksList',
    components: {
        PageTitle, ConfirmDialog, UserTaskDialog
    },
    mixins: [
        pagination,
        tooltipDelay,
        filters,
        dateFormatter,
        customFilters
    ],
    beforeRouteLeave(to, from, next) {
        this.$store.dispatch('userTasks/clearUserTasksFilters')
        next()
    },
    data() {
        return {
            userTask: {},
            userTaskDialog: false,
            deleteUserTaskDialog: false,
            actionLoading: false,
            usersSearch: null,
            usersSearchLoading: false,
            users: [],
            actionName: null,
            action: null,
            headers: [
                {
                    text: 'Temat',
                    value: 'title',
                    sortable: false,
                },
                {
                    text: 'Planowana data ukończenia',
                    value: 'plannedFinishDate',
                    sortable: false,
                    width: 250
                },
                {
                    text: 'Wykonawca',
                    value: 'responsibleUser',
                    sortable: false,
                    width: 200
                },
                {
                    text: 'Data wykonania',
                    value: 'finishDate',
                    sortable: false,
                    width: 200
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        canReadUserTask() {
            return hasUserPermission('user_tasks_admin_read')
        },
        canUpdateUserTask() {
            return hasUserPermission('user_tasks_admin_update')
        },
        canDeleteUserTask() {
            return hasUserPermission('user_tasks_admin_delete')
        },
        canCreateUserTask() {
            return hasUserPermission('user_tasks_admin_create')
        },
        userTasks() {
            return this.$store.state.userTasks.userTasks.userTasks
        },
        userTasksLoading() {
            return this.$store.state.userTasks.userTasks.userTasksLoading
        },
        savedFilters() {
            return this.$store.state.userTasks.userTasks.filters
        },
        filterQuery() {
            const filters = this.filters
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    if (item === 'userTaskStatus') {
                        filters[item] = 10 // NotFinished
                    }
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
    },
    watch: {
        usersSearch(value = null) {
            if (value === null) return

            this.usersSearchLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchUsers(value)
                } else {
                    this.usersSearchLoading = false
                    this.users = []
                }
            }, 500)
        }
    },
    mounted() {
        this.getUserTasks()
        this.$store.dispatch('userTasks/getUserTaskStatuses')
            .catch(error => {
                errorHandler(error)
            })
    },
    methods: {
        searchUsers(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie użytkownika')

            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    this.users = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.usersSearchLoading = false
                })
        },
        getUserTasks() {
            this.$store.dispatch('userTasks/getUserTasks', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showUserTaskDialog() {
            this.actionName = 'Tworzenie nowego zadania'
            this.action = 'createUserTask'
            this.userTaskDialog = true
        },
        closeUserTaskDialog() {
            this.userTaskDialog = false
            setTimeout(() => {
                this.userTask = {}
            }, 500)
        },
        closeDeleteUserTaskDialog() {
            this.deleteUserTaskDialog = false
            setTimeout(() => {
                this.userTask = {}
            }, 500)
        },
        async getUserTask(userTaskId) {
            return await this.$store.dispatch('userTasks/getUserTask', userTaskId)
                .then(response => {
                    this.userTask = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showOpenTask(userTaskId) {
            this.actionName = 'Szczegóły zadania'
            this.action = 'openUserTask'
            this.getUserTask(userTaskId)
                .then(() => {
                    this.userTaskDialog = true
                })
        },
        showEditUserTask(userTaskId) {
            this.actionName = 'Edycja zadania'
            this.action = 'updateUserTask'
            this.getUserTask(userTaskId)
                .then(() => {
                    this.userTaskDialog = true
                })
        },
        showDeleteUserTask(userTaskId) {
            this.getUserTask(userTaskId)
                .then(() => {
                    this.deleteUserTaskDialog = true
                })
        },
        removeUserTask() {
            this.actionLoading = true
            this.$store.dispatch('userTasks/deleteUserTask', this.userTask.id )
                .then(() => {
                    EventBus.$emit('getUserTasksCount')
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.getUserTasks()
                    this.closeDeleteUserTaskDialog()
                })
        },
        changeFilter() {
            this.$store.dispatch('userTasks/setUserTasksFilters', this.filters)
            this.resetPagination()
            this.getUserTasks()
        },
        updatePagination(pagination) {
            this.pagination = pagination

            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending) {
                this.getUserTasks()
            }
            this.clearPaginationPending()
        }
    }
}
</script>
